import React, { useState, useMemo } from "react";
import {
  useNavigate,
  Link,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { adminRoutes } from "../../../routers/AdminRouters";
import {
  useGetEquipmentQuery,
  useGetMusclesQuery,
  useGetSessionFormatQuery,
  useCreateSessionFormatBlockMutation,
} from "../../../services";
import { SessionDifficultyLevel } from "../../../types/workout/sessions/session.type";
import FormatBlock from "./exercisebuildercomponents/FormatBlock";

const SessionBlocksBuilder: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  const { sessionid } = useParams<{ sessionid?: string }>();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const { data: sessionFormat } = useGetSessionFormatQuery(sessionid ?? "");
  const { data: equipmentData } = useGetEquipmentQuery({ limit: -1 });
  const { data: muscleData } = useGetMusclesQuery({ limit: -1 });
  const [createSessionFormatBlock, { isLoading, error }] =
    useCreateSessionFormatBlockMutation();
  // Get formats from sessionFormat.results
  const formatList = sessionFormat?.results || [];
  // Track selected format index
  const [selectedFormatIdx, setSelectedFormatIdx] = useState(0);

  // Track difficulty per format (default to Beginner for each)
  const [formatDifficulties, setFormatDifficulties] = useState<{
    [idx: number]: SessionDifficultyLevel;
  }>({});

  // Toggle to show/hide difficulty options
  const [showDifficultyOptions, setShowDifficultyOptions] = useState(false);

  const selectedFormat = formatList[selectedFormatIdx];
  const selectedFormatId = selectedFormat?._id;
  const hasDifficulties =
    selectedFormat?.format?.difficulties &&
    selectedFormat.format.difficulties.length > 0;

  const currentDifficulty =
    formatDifficulties[selectedFormatIdx] ?? SessionDifficultyLevel.GENERAL;
  const handleDifficultyChange = (
    idx: number,
    level: SessionDifficultyLevel,
  ) => {
    setFormatDifficulties((prev) => ({
      ...prev,
      [idx]: level,
    }));
  };

  const progressPct = 45;
  const themeBox = theme === "dark";
  const borderColor = themeBox ? "#232323" : "#E5E7EB";
  const bgCard = themeBox
    ? "bg-[#121212] border-[#232323]"
    : "bg-white border-gray-200";
  const chipBg = themeBox ? "bg-[#1E1E1E]" : "bg-gray-100";

  // Track format completion and unsaved changes
  const [completedFormats, setCompletedFormats] = useState<Set<number>>(
    new Set(),
  );

  const [showSwitchConfirmation, setShowSwitchConfirmation] = useState(false);
  const [pendingFormatIdx, setPendingFormatIdx] = useState<number | null>(null);

  // Track if current format has dirty changes
  const [isDirty, setIsDirty] = useState(false);

  // Check if current format has unsaved changes
  const hasUnsavedChanges = () => {
    return isDirty;
  };

  // Handle format switching with unsaved changes check
  const handleFormatSwitch = (newFormatIdx: number) => {
    if (newFormatIdx === selectedFormatIdx) return;

    if (hasUnsavedChanges()) {
      // Show confirmation popup
      setPendingFormatIdx(newFormatIdx);
      setShowSwitchConfirmation(true);
    } else {
      // Switch directly
      switchToFormat(newFormatIdx);
    }
  };

  // Switch to new format
  const switchToFormat = (formatIdx: number) => {
    setSelectedFormatIdx(formatIdx);
    // Clear current session data when switching formats
    setBeginnerSessionData([]);
    setAdvancedSessionData([]);
    // Reset difficulty to GENERAL for new format
    setFormatDifficulties((prev) => ({
      ...prev,
      [formatIdx]: SessionDifficultyLevel.GENERAL,
    }));
    // Ensure header shows "Difficulty Options" after switching formats
    setShowDifficultyOptions(false);
    // Reset existing blocks flag
    setHasExistingBlocks(false);
    setIsDirty(false);
  };

  // Save current format and switch
  const saveAndSwitchFormat = async () => {
    if (pendingFormatIdx === null) return;

    try {
      const allWorkouts = [...beginnerSessionData, ...advancedSessionData];

      // Call API if there are unsaved changes
      if (isDirty) {
        await createSessionFormatBlock({
          sessionFormatId: selectedFormat?._id,
          workouts: allWorkouts,
        }).unwrap();
      }

      // Mark current format as completed
      setCompletedFormats((prev) => new Set(prev).add(selectedFormatIdx));

      // Switch to pending format
      switchToFormat(pendingFormatIdx);
    } catch (err) {
      console.error("Failed to save format:", err);
    } finally {
      setShowSwitchConfirmation(false);
      setPendingFormatIdx(null);
    }
  };

  // Discard changes and switch
  const discardAndSwitchFormat = () => {
    if (pendingFormatIdx !== null) {
      switchToFormat(pendingFormatIdx);
    }
    setShowSwitchConfirmation(false);
    setPendingFormatIdx(null);
  };

  const goNext = async () => {
    const allWorkouts = [...beginnerSessionData, ...advancedSessionData];

    const incompleteBefore = formatList
      .map((_: any, idx: number) => idx)
      .filter((idx: number) => !completedFormats.has(idx));
    const isLastRemaining =
      incompleteBefore.length === 1 &&
      incompleteBefore[0] === selectedFormatIdx;

    try {
      if (isDirty) {
        await createSessionFormatBlock({
          sessionFormatId: selectedFormat?._id,
          workouts: allWorkouts,
        }).unwrap();
      }

      // Mark current as completed
      const updatedCompleted = new Set(completedFormats);
      updatedCompleted.add(selectedFormatIdx);
      setCompletedFormats(updatedCompleted);

      // Build target matrix URL using path param + category query
      const baseMatrixUrl = adminRoutes.sessionMetricsBuilder.replace(
        ":sessionid",
        String(sessionid || ""),
      );
      const targetMatrixUrl = `${baseMatrixUrl}${categoryId ? `?category=${encodeURIComponent(categoryId)}` : ""
        }`;

      // If this was the last remaining format, go to matrix page
      if (isLastRemaining) {
        navigate(targetMatrixUrl);
        return;
      }

      // Otherwise, move to next incomplete format in sidebar order
      const remaining = formatList
        .map((_: any, idx: number) => idx)
        .filter((idx: number) => !updatedCompleted.has(idx));

      if (remaining.length > 0) {
        const nextAfter = remaining.find(
          (idx: number) => idx > selectedFormatIdx,
        );
        switchToFormat(nextAfter ?? remaining[0]);
      } else {
        // Fallback: go to matrix page with session id
        navigate(targetMatrixUrl);
      }
    } catch (err) {
      console.error("Failed to save format:", err);
    }
  };

  // Use correct type for beginnerSessionData
  const [beginnerSessionData, setBeginnerSessionData] = useState<
    { workout: string; order: number; difficulty: string }[]
  >([]);
  // Track advanced session data
  const [advancedSessionData, setAdvancedSessionData] = useState<
    { workout: string; order: number; difficulty: string }[]
  >([]);

  const [hasExistingBlocks, setHasExistingBlocks] = useState(false);

  const canProceed =
    beginnerSessionData.length > 0 ||
    advancedSessionData.length > 0 ||
    hasExistingBlocks;

  return (
    <div
      className={`pt-20 pb-8 min-h-screen ${themeBox ? "bg-[#0B0B0B]" : "bg-gray-50"
        } px-4 md:px-6 lg:px-8`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${themeBox ? "text-gray-100" : "text-gray-900"
            }`}
        >
          Session Overview
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={`${adminRoutes.sessionList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Sessions
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            Create New Session
          </span>
        </div>
      </div>

      {/* Main builder card */}
      <div
        className={`rounded-lg border ${bgCard} relative overflow-hidden`}
        style={{ borderColor }}
      >
        {/* Header Row */}
        <div className={`flex border-b ${themeBox ? "border-[#232323]" : "border-gray-200"}`}>
          <div className={`w-64 px-5 py-5 font-semibold text-sm ${themeBox ? "text-gray-100" : "text-gray-800"} border-r ${themeBox ? "border-[#232323]" : "border-gray-200"}`}>
            Session Summary
          </div>
          <div className="flex-1 px-6 py-4">
            <div className={`font-semibold text-sm mb-3 ${themeBox ? "text-gray-100" : "text-gray-800"}`}>
              Create New Session
            </div>
            <div className={`h-1.5 w-full ${themeBox ? "bg-[#1A1A1A]" : "bg-gray-200"} rounded-full overflow-hidden`}>
              <div
                className="h-full bg-[#B6CB24] transition-all duration-500 ease-out shadow-[0_0_8px_rgba(182,203,36,0.4)]"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        <div className="flex">
          {/* SIDEBAR - List all formats and their difficulties */}
          <div
            className={`flex flex-col w-64 border-r ${themeBox ? "border-[#232323]" : "border-gray-200"
              } py-4 ${formatList.length > 8 ? "max-h-[600px] overflow-y-auto custom-scrollbar" : ""}`}
          >
            <style>{`
              .custom-scrollbar::-webkit-scrollbar { width: 4px; }
              .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
              .custom-scrollbar::-webkit-scrollbar-thumb { 
                background: ${theme === "dark" ? "#333333" : "#D1D5DB"}; 
                border-radius: 10px; 
              }
              .custom-scrollbar {
                scrollbar-width: thin;
                scrollbar-color: ${theme === "dark" ? "#333333" : "#D1D5DB"} transparent;
              }
            `}</style>
            <div className="px-5 relative">
              {/* Vertical timeline line */}
              <div
                className="absolute left-[27px] top-2 bottom-4 w-0 border-l border-dashed border-[#444]"
              />

              <div className="space-y-6">
                {formatList.map((fmt: any, idx: number) => {
                  const formatHasDifficulties =
                    fmt?.format?.difficulties &&
                    fmt.format.difficulties.length > 0;
                  const isCompleted = completedFormats.has(idx);
                  const isActive = selectedFormatIdx === idx;

                  return (
                    <div key={fmt._id} className="relative z-10">
                      <div className="flex items-start gap-4">
                        {/* Timeline Dot */}
                        <div
                          className={`mt-2.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${isActive
                            ? "border-[#B6CB24] bg-[#B6CB24] shadow-[0_0_8px_rgba(182,203,36,0.6)]"
                            : isCompleted
                              ? "border-[#B6CB24] bg-[#B6CB24]"
                              : themeBox
                                ? "border-[#444] bg-[#121212]"
                                : "border-gray-300 bg-white"
                            }`}
                        >
                          {isCompleted && !isActive && (
                            <svg
                              width="8"
                              height="8"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M2.5 6L5 8.5L9.5 3.5"
                                stroke="black"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>

                        {/* Format Content */}
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleFormatSwitch(idx)}
                            className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${isActive
                              ? themeBox
                                ? "bg-[#B6CB24]/10 border-[#B6CB24] text-[#F3F3F6]"
                                : "bg-lime-50 border-lime-500 text-lime-900"
                              : themeBox
                                ? "bg-transparent border-[#232323] text-gray-400 hover:border-[#444]"
                                : "bg-transparent border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            <span className="text-sm font-semibold truncate block">
                              {fmt.name}
                            </span>
                          </button>

                          {/* Difficulty sub-items aligned under format name */}
                          {formatHasDifficulties && isActive && (
                            <div className="mt-4 flex flex-col items-center space-y-3">
                              <button
                                onClick={() =>
                                  handleDifficultyChange(
                                    idx,
                                    SessionDifficultyLevel.BEGINNER,
                                  )
                                }
                                className={`text-sm transition-colors ${currentDifficulty ===
                                  SessionDifficultyLevel.BEGINNER
                                  ? "text-[#B6CB24] font-bold"
                                  : "text-gray-500 hover:text-gray-400"
                                  }`}
                              >
                                Beginner
                              </button>
                              <button
                                onClick={() =>
                                  handleDifficultyChange(
                                    idx,
                                    SessionDifficultyLevel.ADVANCED,
                                  )
                                }
                                className={`text-sm transition-colors ${currentDifficulty ===
                                  SessionDifficultyLevel.ADVANCED
                                  ? "text-[#B6CB24] font-bold"
                                  : "text-gray-500 hover:text-gray-400"
                                  }`}
                              >
                                Advanced
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Middle Content: only difficulty toggle + FormatBlock */}
          <div className="flex-1">
            {/* Header Difficulty block: show toggle button and difficulty selection when hasDifficulties */}
            {hasDifficulties && (
              <div className="px-6 py-4 flex items-center gap-6">
                {/* Difficulty Options: visible only when toggle is OFF */}
                {!showDifficultyOptions && (
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-sm font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                        }`}
                    >
                      Difficulty Options
                    </span>
                    <button
                      onClick={() => setShowDifficultyOptions(true)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${themeBox ? "bg-[#2A2A2A]" : "bg-gray-300"
                        }`}
                    >
                      <span className="absolute top-1 left-1 h-4 w-4 rounded-full bg-white shadow-sm transition-all" />
                    </button>
                  </div>
                )}

                {/* Show "Choose Difficulty" only when toggle is ON */}
                {showDifficultyOptions && (
                  <>
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-sm font-medium ${themeBox ? "text-gray-300" : "text-gray-700"
                          }`}
                      >
                        Choose Difficulty
                      </span>
                      {/* Quick toggle between Beginner/Advanced */}
                      <button
                        onClick={() =>
                          setFormatDifficulties((prev) => ({
                            ...prev,
                            [selectedFormatIdx]:
                              currentDifficulty ===
                                SessionDifficultyLevel.BEGINNER
                                ? SessionDifficultyLevel.ADVANCED
                                : SessionDifficultyLevel.BEGINNER,
                          }))
                        }
                        className={`relative w-11 h-6 rounded-full transition-colors ${currentDifficulty === SessionDifficultyLevel.BEGINNER
                          ? "bg-[#B6CB24]"
                          : themeBox
                            ? "bg-[#2A2A2A]"
                            : "bg-gray-300"
                          }`}
                      >
                        <span
                          className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${currentDifficulty ===
                            SessionDifficultyLevel.BEGINNER
                            ? "left-1"
                            : "left-6"
                            }`}
                        />
                      </button>
                    </div>

                    <div
                      className={`flex rounded-full overflow-hidden border ${themeBox ? "border-[#2A2A2A]" : "border-gray-300"
                        }`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setFormatDifficulties((prev) => ({
                            ...prev,
                            [selectedFormatIdx]:
                              SessionDifficultyLevel.BEGINNER,
                          }))
                        }
                        className={`px-6 py-1.5 text-sm font-medium transition-colors ${currentDifficulty === SessionDifficultyLevel.BEGINNER
                          ? "bg-[#B6CB24] text-black"
                          : themeBox
                            ? "bg-transparent text-gray-400 hover:bg-[#1A1A1A]"
                            : "bg-transparent text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        Beginner
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormatDifficulties((prev) => ({
                            ...prev,
                            [selectedFormatIdx]:
                              SessionDifficultyLevel.ADVANCED,
                          }))
                        }
                        className={`px-6 py-1.5 text-sm font-medium transition-colors ${currentDifficulty === SessionDifficultyLevel.ADVANCED
                          ? "bg-[#B6CB24] text-black"
                          : themeBox
                            ? "bg-transparent text-gray-400 hover:bg-[#1A1A1A]"
                            : "bg-transparent text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        Advanced
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <FormatBlock
              themeBox={themeBox}
              muscles={muscleData || []}
              equipments={equipmentData || []}
              formatId={selectedFormatId}
              difficulty={currentDifficulty}
              beginnerSessionData={beginnerSessionData}
              setBeginnerSessionData={setBeginnerSessionData}
              advancedSessionData={advancedSessionData}
              setAdvancedSessionData={setAdvancedSessionData}
              currentDifficulty={currentDifficulty}
              setHasExistingBlocks={setHasExistingBlocks}
              onDirtyChange={setIsDirty}
            />
          </div>
        </div>

        {/* Save Confirmation Modal */}
        {showSwitchConfirmation && (
          <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"
              }`}
          >
            <div
              className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark"
                ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
                : "bg-white text-gray-900 border-gray-200"
                }`}
            >
              <div className="px-6 py-6 text-center">
                <div
                  className={`mx-auto mb-3 flex items-center justify-center w-12 h-12 rounded-full ${theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                    }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">Save Changes?</h3>
                <p
                  className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"
                    } mb-6`}
                >
                  You have unsaved changes in the current format. Do you want to
                  save them before switching?
                </p>

                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={saveAndSwitchFormat}
                    disabled={isLoading}
                    className="px-4 py-2 font-medium hover:opacity-90 text-black rounded-[10px] border disabled:opacity-50"
                    style={{
                      border: "1px solid var(--menu-active-border, #C6D92C)",
                      background:
                        "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                    }}
                  >
                    {isLoading ? "Saving..." : "Save & Switch"}
                  </button>
                  <button
                    type="button"
                    onClick={discardAndSwitchFormat}
                    className="px-4 py-2 font-medium hover:opacity-90 text-white rounded-[10px] border"
                    style={{
                      border: "1px solid #D92C2C",
                      background:
                        "radial-gradient(94.17% 100% at 52.48% 0%, rgba(217, 44, 44, 0.80) 0%, rgba(91, 16, 16, 0.80) 100%)",
                    }}
                  >
                    Discard & Switch
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Buttons */}
        <div
          className={`flex items-center justify-center px-6 py-4 border-t ${themeBox ? "border-[#232323]" : "border-gray-200"
            }`}
        >
          <div className="flex items-center gap-4">
            <button
              onClick={goNext}
              disabled={!canProceed}
              className={`flex items-center gap-2 px-5 py-1.5 rounded-[10px] border border-[#C6D92C] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed ${theme === "dark"
                ? "bg-[#232323] text-gray-200 hover:bg-[#333333]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.80) 0%, rgba(83,91,16,0.80) 100%)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M19 21C19.6068 20.4102 22 18.8403 22 18C22 17.1597 19.6068 15.5898 19 15M21 18H14"
                  stroke="#C6D92C"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 21C7.28595 21 4.92893 21 3.46447 19.5355C2 18.0711 2 15.714 2 11V7.94427C2 6.1278 2 5.21956 2.38032 4.53806C2.65142 4.05227 3.05227 3.65142 3.53806 3.38032C4.21956 3 5.1278 3 6.94427 3C8.10802 3 8.6899 3 9.19926 3.19101C10.3622 3.62712 10.8418 4.68358 11.3666 5.73313L12 7M8 7H16.75C18.8567 7 19.91 7 20.6667 7.50559C20.9943 7.72447 21.2755 8.00572 21.4944 8.33329C21.9796 9.05942 21.9992 10.0588 22 12V13"
                  stroke="#F3F3F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              {hasExistingBlocks ? "Update & Next" : "Save & Next"}
            </button>
            <button
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              onClick={() =>
                navigate(
                  `${adminRoutes.sessionFormatBuilder.replace(
                    ":sessionid",
                    String(sessionid || ""),
                  )}${categoryId
                    ? `?category=${encodeURIComponent(categoryId)}`
                    : ""
                  }`,
                )
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11 6H15.5C17.9853 6 20 8.01472 20 10.5C20 12.9853 17.9853 15 15.5 15H4"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.99998 12C6.99998 12 4.00001 14.2095 4 15C3.99999 15.7906 7 18 7 18"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Previous
            </button>

            <button
              onClick={() => navigate(adminRoutes.sessionList)}
              className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium border ${themeBox
                ? "border-[#444] text-gray-300 hover:bg-[#1E1E1E]"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 9L9 14.9996M15 15L9 9.00039"
                  stroke={themeBox ? "#F3F3F6" : "#C6D92C"}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Cancel
            </button>
          </div>
        </div>
        {isLoading && (
          <div className="text-xs text-gray-500 px-6 py-2">Saving...</div>
        )}
        {error && (
          <div className="text-xs text-red-500 px-6 py-2">
            Error saving session.
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionBlocksBuilder;
